import type {
  Channel,
  ChannelContent,
  MergeValues,
  MessageOption,
  MessageSource,
  MessageType,
  OptionsFormat,
  RenderedMessage,
} from '@/api/Rover/notifications/roverNotifications.types';

/**
 * Shared rules for Rover status notifications.
 *
 * The UI uses these for instant previews. The backend must follow the same rules
 * when it really sends. The cases in `renderMessage.cases.json` are the shared
 * contract: run them against the server renderer too.
 */

export const SELECTED_OPTIONS_TOKEN = '{{selected_options}}';
const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

const isSet = (v: string | null | undefined): v is string => typeof v === 'string' && v.trim().length > 0;

/** Does this channel have its own custom text? */
export function hasCustomContent(channel: Channel, content: ChannelContent): boolean {
  switch (channel) {
    case 'email':
      return isSet(content.email.subject) || isSet(content.email.body);
    case 'sms':
      return isSet(content.sms.body);
    case 'onscreen':
      return isSet(content.onscreen.title) || isSet(content.onscreen.body);
  }
}

/** Which message is used: options (merged into custom or default) → custom → default. */
export function resolveSource(channel: Channel, content: ChannelContent, activeOptionCount: number): MessageSource {
  if (activeOptionCount > 0) return 'options';
  return hasCustomContent(channel, content) ? 'custom' : 'default';
}

/** Custom text per field, falling back to the default field by field. */
export function resolveFields(
  channel: Channel,
  content: ChannelContent,
  defaults: ChannelContent
): { subject?: string; title?: string; body: string } {
  const pick = (custom: string | null, fallback: string | null) => (isSet(custom) ? custom : (fallback ?? ''));
  switch (channel) {
    case 'email':
      return {
        subject: pick(content.email.subject, defaults.email.subject),
        body: pick(content.email.body, defaults.email.body),
      };
    case 'sms':
      return { body: pick(content.sms.body, defaults.sms.body) };
    case 'onscreen':
      return {
        title: pick(content.onscreen.title, defaults.onscreen.title),
        body: pick(content.onscreen.body, defaults.onscreen.body),
      };
  }
}

/** How the selected options read in a given channel. */
export function formatSelectedOptions(
  channel: Channel,
  selected: Pick<MessageOption, 'label' | 'messageText'>[],
  format: OptionsFormat
): string {
  if (selected.length === 0) return '';
  if (channel === 'sms') return selected.map((o) => o.label).join(', ');
  if (format === 'bullets') return selected.map((o) => `• ${o.messageText}`).join('\n');
  return selected.map((o) => o.messageText).join(' ');
}

/** Replace merge fields. Unknown tokens stay as written and are reported. */
export function applyMergeFields(text: string, values: MergeValues, unknown: Set<string>): string {
  return text.replace(TOKEN_RE, (match, key: string) => {
    const v = (values as Record<string, string | undefined>)[key];
    if (v === undefined) {
      unknown.add(key);
      return match;
    }
    return v;
  });
}

/**
 * Put selected options into the body: at {{selected_options}} if present, otherwise
 * appended at the end (blank line before it; a single space for SMS).
 */
export function insertSelectedOptions(body: string, formatted: string, channel: Channel): string {
  if (/\{\{\s*selected_options\s*\}\}/.test(body)) {
    return body.replace(/\{\{\s*selected_options\s*\}\}/g, formatted);
  }
  if (!formatted) return body;
  if (!body.trim()) return formatted;
  return channel === 'sms' ? `${body.trimEnd()} ${formatted}` : `${body.trimEnd()}\n\n${formatted}`;
}

const GSM7_RE = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?¡A-ZÄÖÑÜ§¿a-zäöñüà^{}\\[~\]|€]*$/;

/** SMS length the way Twilio bills it: GSM-7 160/153, otherwise UCS-2 70/67. */
export function smsSegments(text: string): { characters: number; segments: number; encoding: 'GSM-7' | 'UCS-2' } {
  const gsm = GSM7_RE.test(text);
  const characters = [...text].length;
  const single = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67;
  const segments = characters === 0 ? 0 : characters <= single ? 1 : Math.ceil(characters / multi);
  return { characters, segments, encoding: gsm ? 'GSM-7' : 'UCS-2' };
}

export interface RenderInput {
  channel: Channel;
  content: ChannelContent;
  defaults: ChannelContent;
  mergeValues: MergeValues;
  /** Active options on the status (only the count matters for the source). */
  activeOptionCount: number;
  selectedOptions: Pick<MessageOption, 'label' | 'messageText'>[];
  optionsFormat: OptionsFormat;
}

/** Render one channel's message, end to end. */
export function renderMessage(input: RenderInput): RenderedMessage {
  const { channel, content, defaults, mergeValues, activeOptionCount, selectedOptions, optionsFormat } = input;
  const source = resolveSource(channel, content, activeOptionCount);
  const fields = resolveFields(channel, content, defaults);
  const unknown = new Set<string>();

  const bodyOptions = formatSelectedOptions(channel, selectedOptions, optionsFormat);
  // In one-line fields (subject, title) options always read as a comma list of labels.
  const inlineOptions = selectedOptions.map((o) => o.label).join(', ');

  const withOptions = source === 'options' ? insertSelectedOptions(fields.body, bodyOptions, channel) : fields.body;
  const values: MergeValues = { ...mergeValues, selected_options: source === 'options' ? inlineOptions : '' };
  const body = applyMergeFields(withOptions, values, unknown);

  const result: RenderedMessage = { channel, source, body, unknownTokens: [...unknown] };
  if (fields.subject !== undefined) result.subject = applyMergeFields(fields.subject, values, unknown);
  if (fields.title !== undefined) result.title = applyMergeFields(fields.title, values, unknown);
  result.unknownTokens = [...unknown];
  if (channel === 'sms') result.sms = smsSegments(body);
  return result;
}

export const EMPTY_CONTENT: ChannelContent = {
  email: { subject: null, body: null },
  sms: { body: null },
  onscreen: { title: null, body: null },
};

/**
 * Render one channel using its chosen message type (default / custom / options).
 * - default: custom text is ignored (but kept, so switching back restores it)
 * - custom: custom text, falling back to the default field by field
 * - options: picks merged into the custom text, or into the default when there is none
 */
export function renderForMessageType(input: RenderInput & { messageType: MessageType }): RenderedMessage {
  const { messageType, ...rest } = input;
  return renderMessage({
    ...rest,
    content: messageType === 'default' ? EMPTY_CONTENT : rest.content,
    activeOptionCount: messageType === 'options' ? rest.activeOptionCount : 0,
    selectedOptions: messageType === 'options' ? rest.selectedOptions : [],
  });
}

/** The status-change modal opens when an enabled channel uses options and the status has active options. */
export function statusRequiresOptions(
  enabled: Record<Channel, boolean>,
  messageType: Record<Channel, MessageType>,
  activeOptionCount: number
): boolean {
  if (activeOptionCount < 1) return false;
  return (['email', 'sms', 'onscreen'] as Channel[]).some((c) => enabled[c] && messageType[c] === 'options');
}

/** Summary badge for a status: options if any enabled channel uses them, else custom if any, else default. */
export function summarizeSource(enabled: Record<Channel, boolean>, messageType: Record<Channel, MessageType>): MessageSource {
  const used = (['email', 'sms', 'onscreen'] as Channel[]).filter((c) => enabled[c]).map((c) => messageType[c]);
  if (used.includes('options')) return 'options';
  if (used.includes('custom')) return 'custom';
  return 'default';
}

/** Used when a saved row has no explicit per-channel message type yet. */
export function inferChannelMessageType(content: ChannelContent, activeOptionCount: number): Record<Channel, MessageType> {
  const one = (channel: Channel): MessageType => {
    if (hasCustomContent(channel, content)) return 'custom';
    if (activeOptionCount > 0) return 'options';
    return 'default';
  };
  return { email: one('email'), sms: one('sms'), onscreen: one('onscreen') };
}

/** Is a set of picks valid for the status's selection rules? */
export function isSelectionValid(requiresOptions: boolean, mode: 'single' | 'multiple', count: number): boolean {
  if (!requiresOptions) return true;
  if (count < 1) return false;
  return mode === 'multiple' || count === 1;
}
